// Cold-start archived-session cache — persist the last-known archived-session page
// (keyed by workspace id) so a fresh launch / workspace switch renders the archive
// immediately instead of "No archived sessions" until the daemon connects.
//
// SAME gap, same cause, same fix as agent-roster-cache.ts: cyborg:list_archived_sessions
// is DAEMON_FORWARD, so it answers [] while the embedded daemon is still attaching to
// the relay, and nothing server-side can answer it offline. Worse than the roster's
// case, though: two of the three surfaces have no loading branch at all
// (routes/workspace/[id]/dms/+page.svelte, SessionResumePicker.svelte) and
// SessionState.loading initializes to false, so the cold window renders the
// "No archived sessions" EMPTY STATE — a wrong answer shown as fact, not a spinner.
// The only mitigation today is retrySessionsOnEmpty (app.svelte.ts): 8 attempts on a
// 1s→5s backoff, i.e. up to ~28s of empty archive on every cold start.
//
// Merge semantics are REUSED, not reinvented. nextAgentSnapshot (daemon-status.ts)
// called WITHOUT silentDaemonIds reduces to exactly the rule this needs — a live
// answer always wins EXCEPT an empty one that arrived while no daemon was reachable.
// shouldPersistRoster (agent-roster-cache.ts) is the same rule for the write side, so
// the "an offline [] is not an answer" invariant keeps ONE definition in this repo
// rather than a third copy here.
//
// OWNER-KEYED rather than logout-wiped. disconnectFromServer clears sessionState in
// memory (app.svelte.ts) but wipes no localStorage, and a crash / force-quit never
// runs logout at all — so this payload (session titles + absolute cwds) would outlive
// the account that produced it on a shared device. This mirrors cacheInit's appMeta
// user guard (cache/index.ts) and is keyed on the EMAIL for the reason documented
// there: one human has two ids (the cloud relay's PG uuid vs a daemon-direct
// canonicalUserId), so an id compare reads a cloud↔self-host switch as a different
// user. An owner mismatch reads as a MISS, which stays correct when no logout ran.
//
// Kept as small, storage-injected functions (no live-store imports) so the read/write
// logic runs under plain-node vitest — same shape as agent-roster-cache.ts.

import type { ArchivedSession } from "../ws-client-types.js";
import { rosterCacheOwner, shouldPersistRoster, type RosterStore } from "./agent-roster-cache.js";

const KEY_PREFIX = "cyborg7:sessionList:";

// Storage cap, mirroring ARCHIVED_SESSIONS_PAGE_SIZE (the server page size). The
// cold-start paint only ever needs page 1, so "Show more" pages are deliberately not
// persisted and a long browse can't grow this entry without bound.
// ponytail: fixed cap + one entry per workspace, no LRU — ≤30 small flat rows each.
// If this ever needs a byte budget, that's the signal to move it to the IndexedDB
// layer (lib/cache/) which already has quota handling and LRU eviction.
const MAX_CACHED_SESSIONS = 30;

export function sessionListCacheKey(workspaceId: string): string {
  return KEY_PREFIX + workspaceId;
}

// The stored envelope: the rows PLUS the account they belong to.
interface CachedSessionList {
  owner: string;
  sessions: ArchivedSession[];
}

// Normalize an email into the cache's owner key. Same rule as cacheInit's ownerKey
// (cache/index.ts) so the two guards can't disagree about who a cache belongs to —
// now literally the same function as the roster cache's, which is why this delegates
// instead of restating it.
export const sessionCacheOwner = rosterCacheOwner;

// Parse a persisted blob for `owner`. Returns [] for absent / malformed / non-object
// input, for a blob written by a DIFFERENT account, and for an unknown owner — so a
// first-ever launch, a corrupted entry, and a shared device all render no cold-start
// rows rather than throwing or leaking. Drops entries without the string `id` that the
// row render + dedupe key on, so a partial-corrupt cache can't render broken rows.
export function parseSessionListCache(
  raw: string | null | undefined,
  owner: string,
): ArchivedSession[] {
  if (!raw || !owner) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return []; // intentional: a corrupt cache → no cold-start rows; the live fetch backfills.
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return [];
  const envelope = parsed as Partial<CachedSessionList>;
  // Owner mismatch (or a pre-envelope blob with no owner at all) is a MISS, not a read.
  if (envelope.owner !== owner) return [];
  if (!Array.isArray(envelope.sessions)) return [];
  return envelope.sessions.filter(
    (s): s is ArchivedSession =>
      typeof s === "object" && s !== null && typeof (s as { id?: unknown }).id === "string",
  );
}

export function loadSessionListCache(
  store: RosterStore,
  workspaceId: string,
  email: string | null | undefined,
): ArchivedSession[] {
  const owner = sessionCacheOwner(email);
  if (!store || !owner) return [];
  try {
    return parseSessionListCache(store.getItem(sessionListCacheKey(workspaceId)), owner);
  } catch {
    return []; // intentional: storage disabled/blocked → no cold-start rows this launch.
  }
}

// Persist the SERVER's answer (never our own cache — that would pin a stale page
// forever). `daemonConnected` decides whether an empty answer is authoritative.
export function saveSessionListCache(
  store: RosterStore,
  workspaceId: string,
  email: string | null | undefined,
  sessions: ArchivedSession[],
  daemonConnected: boolean,
): void {
  const owner = sessionCacheOwner(email);
  // No owner ⇒ we can't prove whose rows these are, so we never write them.
  if (!store || !owner) return;
  // An EMPTY list that arrived while no daemon was reachable is "couldn't reach the
  // daemon", not "no archived sessions" — persisting it would wipe the page the next
  // cold start needs. A non-empty list is always real; an empty one is authoritative
  // only when a daemon is reachable (a genuine clear → overwrite so nothing ghosts).
  if (!shouldPersistRoster(sessions, daemonConnected)) return;
  try {
    store.setItem(
      sessionListCacheKey(workspaceId),
      JSON.stringify({
        owner,
        sessions: sessions.slice(0, MAX_CACHED_SESSIONS),
      } satisfies CachedSessionList),
    );
  } catch {
    // intentional: storage full/disabled → skip caching; the cold start just shows the
    // empty archive until the daemon answers, exactly as it does today.
  }
}
